import { EmailAuditRow, EmailAuditStatus } from '../../../schemas/EmailAudit.schema';

// Error statuses (a recipient's latest attempt in one of these = a delivery
// problem). Kept in sync with the aggregate rollup on the request list.
const ERROR_STATUSES = new Set<EmailAuditStatus>(['bounced', 'complaint', 'rejected', 'failed']);

export function isErrorStatus(status: EmailAuditStatus): boolean {
  return ERROR_STATUSES.has(status);
}

// Recency key for "latest attempt": prefer the send timestamp, fall back to the
// row's updatedAt so callback-only rows still order sensibly.
function attemptTime(row: EmailAuditRow): number {
  const ts = row.emailSentAt ?? row.updatedAt;
  const parsed = Date.parse(ts);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export interface CollapsedEmailAudits {
  rows: EmailAuditRow[];
  // True when every row for the request is a skip row (firm email suppressed in
  // favor of a same-day universal send) — the modal shows an info state instead
  // of an empty grid.
  hasOnlySkipRows: boolean;
}

// One row per recipient (their latest non-skip attempt), ordered failures
// first, then To before Cc, then email alphabetically.
export function collapseToLatestPerRecipient(rows: EmailAuditRow[]): CollapsedEmailAudits {
  const nonSkip = rows.filter((r) => !r.skipped);
  const hasOnlySkipRows = rows.length > 0 && nonSkip.length === 0;

  const latestByEmail = new Map<string, EmailAuditRow>();
  for (const row of nonSkip) {
    const existing = latestByEmail.get(row.recipientEmail);
    if (!existing || attemptTime(row) >= attemptTime(existing)) {
      latestByEmail.set(row.recipientEmail, row);
    }
  }

  const collapsed = Array.from(latestByEmail.values()).sort((a, b) => {
    const aErr = isErrorStatus(a.emailStatus) ? 0 : 1;
    const bErr = isErrorStatus(b.emailStatus) ? 0 : 1;
    if (aErr !== bErr) {
      return aErr - bErr;
    }
    if (a.recipientType !== b.recipientType) {
      return a.recipientType === 'to' ? -1 : 1;
    }
    return a.recipientEmail.localeCompare(b.recipientEmail);
  });

  return { rows: collapsed, hasOnlySkipRows };
}

const STATUS_NOTE: Record<EmailAuditStatus, string> = {
  bounced: 'Bounced — the address may be invalid or the mailbox unreachable',
  complaint: 'Recipient marked the email as spam',
  rejected: 'Rejected by the email provider before sending',
  failed: 'Sending failed',
  delayed: 'Delivery delayed — the provider is retrying',
  delivered: 'Delivered',
  sent: 'Sent — delivery not yet confirmed',
};

export function deriveEmailAuditNote(row: EmailAuditRow): string {
  if (row.skipped) {
    return 'Skipped — covered by a universal request email sent the same day';
  }
  const base = STATUS_NOTE[row.emailStatus];
  return row.reminder ? `${base} (reminder)` : base;
}
